
import { useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import type { Profile, Demographics } from '../types';
import type { Json } from '@/integrations/supabase/types';

/**
 * Hook for updating user profile data
 */
export const useProfileUpdating = (
  userId: string | undefined,
  currentProfile: Profile | null,
  setProfile: (profile: Profile | null) => void
) => {
  const [isUpdating, setIsUpdating] = useState(false);

  /**
   * Update user profile information
   */
  const updateProfile = async (profileData: Partial<Profile>): Promise<Profile | null> => {
    if (!userId) {
      console.error('Cannot update profile: No user ID provided');
      toast.error('You must be logged in to update your profile');
      return Promise.reject('No user ID');
    }

    try {
      setIsUpdating(true);
      console.log('Updating profile for user:', userId);
      console.log('Profile data being sent:', profileData);

      // Use the profiles table in the public schema - no direct access to auth.users
      const { data, error } = await supabase
        .from('profiles')
        .update({
          name: profileData.name,
          email: profileData.email,
          phone_number: profileData.phone_number,
          country_code: profileData.country_code,
          constituency_code: profileData.constituency_code,
          updated_at: new Date().toISOString()
        })
        .eq('id', userId)
        .select('*')
        .single();

      if (error) {
        console.error('Error updating profile:', error);
        toast.error('Failed to update profile: ' + error.message);
        throw error;
      }

      console.log('Profile updated successfully:', data);
      
      const updatedProfile = data as Profile;
      setProfile(updatedProfile);
      toast.success('Profile updated successfully');
      
      return updatedProfile;
    } catch (error: any) {
      console.error('Error in updateProfile:', error);
      toast.error(error.message || 'An error occurred while updating your profile');
      throw error;
    } finally {
      setIsUpdating(false);
    }
  };

  /**
   * Update user demographics information
   */
  const updateDemographics = async (demographics: Demographics): Promise<Profile | null> => {
    if (!userId) {
      console.error('Cannot update demographics: No user ID provided');
      toast.error('You must be logged in to update your demographics');
      return Promise.reject('No user ID');
    }

    try {
      setIsUpdating(true);
      console.log('Updating demographics for user:', userId);

      // Cast demographics to Json type to satisfy TypeScript
      const demographicsJson = demographics as unknown as Json;

      // Update the demographics JSON field in the profiles table
      const { data, error } = await supabase
        .from('profiles')
        .update({
          demographics: demographicsJson,
          updated_at: new Date().toISOString()
        })
        .eq('id', userId)
        .select('*')
        .single();

      if (error) {
        console.error('Error updating demographics:', error);
        toast.error('Failed to update demographics: ' + error.message);
        throw error;
      }

      console.log('Demographics updated successfully:', data);
      
      const updatedProfile = data as Profile;
      setProfile(updatedProfile);
      toast.success('Demographics updated successfully');
      
      return updatedProfile;
    } catch (error: any) {
      console.error('Error in updateDemographics:', error);
      toast.error(error.message || 'An error occurred while updating your demographics');
      throw error;
    } finally {
      setIsUpdating(false);
    }
  };

  return {
    updateProfile,
    updateDemographics,
    isUpdating
  };
};
